// app-kit i18n: (1) a registration primitive so non-component glue (surfaceApiError)
// can translate outside a component, and (2) the shared vocabulary catalogs + merge.
//
// (1) Registration — each app calls registerAppKitI18n(i18n.global) once in main.ts;
// t()/te() delegate to it and echo the key before registration (never throw).
export interface AppKitI18n {
  t(key: string): string;
  t(key: string, named: Record<string, unknown>): string;
  te(key: string): boolean;
}
let composer: AppKitI18n | null = null;
export function registerAppKitI18n(instance: AppKitI18n): void {
  composer = instance;
}
export function t(key: string, named?: Record<string, unknown>): string {
  if (!composer) return key;
  return named ? composer.t(key, named) : composer.t(key);
}
export function te(key: string): boolean {
  return composer ? composer.te(key) : false;
}

// (2) Shared i18n vocabulary catalogs, keyed by locale (ADR 0026 §7).
import de from './de';
import en from './en';
import es from './es';
import fr from './fr';
import it from './it';
import nl from './nl';
import pl from './pl';
import pt from './pt';
import sv from './sv';

export const appKitMessages = { de, en, es, fr, it, nl, pl, pt, sv } as const;
export type AppKitLocale = keyof typeof appKitMessages;

type Messages = Record<string, unknown>;

// Recursive merge (objects merge, leaves overwrite) — layers an app's own catalog
// on top of the shared vocabulary.
function deepMerge(base: Messages, over: Messages): Messages {
  const out: Messages = { ...base };
  for (const [k, v] of Object.entries(over)) {
    const b = out[k];
    if (b && typeof b === 'object' && !Array.isArray(b) && v && typeof v === 'object' && !Array.isArray(v)) {
      out[k] = deepMerge(b as Messages, v as Messages);
    } else {
      out[k] = v;
    }
  }
  return out;
}

// Layer the shared app-kit vocabulary UNDER each app locale's own messages, for the
// locales the app actually ships (apprentice-ui is de-only → only its de is merged).
// The no-shadow check guarantees an app never redefines an app-kit key, so this is
// purely additive. Use at createI18n: `messages: mergeAppKitMessages({ de, en, … })`.
export function mergeAppKitMessages<T extends Record<string, Messages>>(appMessages: T): T {
  const out = {} as Record<string, Messages>;
  for (const [loc, msgs] of Object.entries(appMessages)) {
    const base = (appKitMessages as Record<string, Messages>)[loc];
    out[loc] = base ? deepMerge(base, msgs) : msgs;
  }
  return out as T;
}
